import { useContext, useMemo, useCallback } from 'react';
import { TradingContext } from '../contexts/TradingContext';
import { useEnforcementAlerts } from '../contexts/EnforcementAlertsContext';
import useTradingHoursRestriction from './useTradingHoursRestriction';
import { formatLockoutExpiresAt } from '../utils/lockoutTimeFormat';

/**
 * Hook for checking trading lockout status.
 *
 * Lockout state is derived from active enforcement alerts (EnforcementAlertsContext).
 * An account is locked when it has an undismissed, non-expired lockout row.
 */
const useTradingLockout = () => {
  const { selectedAccount } = useContext(TradingContext);
  const { isAccountLockedOut, getAccountLockout, pendingAlerts, fetchPendingAlerts } = useEnforcementAlerts();

  const {
    canTrade: canTradeByRestriction,
    lockoutMessage: restrictionMessage
  } = useTradingHoursRestriction();

  const selectedAccountLockout = useMemo(() => {
    if (!selectedAccount?.AccountID) return null;
    return getAccountLockout(selectedAccount.AccountID);
  }, [selectedAccount?.AccountID, getAccountLockout]);

  const isTradingLocked = useMemo(() => {
    if (!selectedAccount?.AccountID) return false;
    return isAccountLockedOut(selectedAccount.AccountID) || !canTradeByRestriction;
  }, [selectedAccount?.AccountID, isAccountLockedOut, canTradeByRestriction]);

  const lockoutExpiresAt = selectedAccountLockout?.expires_at || null;

  const lockoutExpiresAtFormatted = useMemo(() => {
    return formatLockoutExpiresAt(lockoutExpiresAt, 'long');
  }, [lockoutExpiresAt]);

  const lockoutMessage = useMemo(() => {
    if (!canTradeByRestriction && restrictionMessage) {
      return restrictionMessage;
    }
    if (!selectedAccountLockout) return null;

    const expiryText = lockoutExpiresAtFormatted ? ` until ${lockoutExpiresAtFormatted}` : '';
    const ruleType = selectedAccountLockout.rule_type;

    switch (ruleType) {
      case 'manual_lockout':
        return `Trading disabled${expiryText}: Manual lockout enabled`;
      case 'max_loss_per_day': {
        const maxLoss = selectedAccountLockout.threshold_value;
        return `Trading disabled${expiryText}: Daily loss limit of $${parseFloat(maxLoss || 0).toFixed(2)} exceeded`;
      }
      case 'max_trades_per_day': {
        const max = selectedAccountLockout.threshold_value;
        return `Trading disabled${expiryText}: Maximum ${max} daily trades reached`;
      }
      case 'trading_window':
        return `Trading disabled${expiryText}: Outside allowed trading hours`;
      default:
        return `Trading disabled${expiryText}: ${selectedAccountLockout.message || 'Rule enforcement active'}`;
    }
  }, [canTradeByRestriction, restrictionMessage, selectedAccountLockout, lockoutExpiresAtFormatted]);

  // Position-specific pending alert for the selected account (for position loss warnings)
  const pendingTradeAlert = useMemo(() => {
    if (!selectedAccount?.AccountID) return null;
    return pendingAlerts.find(
      a => a.account_id === selectedAccount.AccountID && a.rule_type === 'max_loss_per_position'
    ) || null;
  }, [selectedAccount?.AccountID, pendingAlerts]);

  const positionCloseMessage = useMemo(() => {
    if (!pendingTradeAlert) return null;
    const maxLoss = pendingTradeAlert.threshold_value;
    return `Position exceeds max loss per position of $${parseFloat(maxLoss || 0).toFixed(2)}`;
  }, [pendingTradeAlert]);

  const refreshLockoutStatus = useCallback(() => {
    fetchPendingAlerts();
  }, [fetchPendingAlerts]);

  return {
    isTradingLocked,
    isDailyLossLimitExceeded: isTradingLocked,
    isPositionLossLimitExceeded: !!pendingTradeAlert,
    lockoutMessage,
    positionCloseMessage,
    lockoutExpiresAt,
    lockoutExpiresAtFormatted,
    selectedAccountLockout,
    pendingDailyAlert: selectedAccountLockout?.rule_type === 'max_loss_per_day' ? selectedAccountLockout : null,
    pendingTradeAlert,
    lockedOutAccounts: [],
    refreshLockoutStatus
  };
};

export default useTradingLockout;
